import React, { useState } from 'react';
import {
    Box,
    Modal,
    Typography,
    IconButton,
    CircularProgress,
    Chip,
    Stack,
    Divider,
    Paper,
    TextField,
    Button
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { GoAlertFill } from "react-icons/go";

// 🔥 CAMBIO CLAVE: Importamos tu configuración global de Axios
import api from "../../assets/utils/axiosConfig";

const ModalAlerta = ({ vehiculoId }) => {
    const [open, setOpen] = useState(false);
    const [alertas, setAlertas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagina, setPagina] = useState(1); // página actual
    const [total, setTotal] = useState(0);   // total de alertas recibidas

    const [fechaInicio, setFechaInicio] = useState(null);
    const [fechaFin, setFechaFin] = useState(null);

    const LIMITE = 50;

    const handleOpen = () => {
        const hoy = new Date();
        const hace7dias = new Date();
        hace7dias.setDate(hoy.getDate() - 7);

        setFechaInicio(hace7dias);
        setFechaFin(hoy);
        setPagina(1);
        setOpen(true);

        fetchAlertasCustom(hace7dias, hoy, 1);
    };

    const handleClose = () => setOpen(false);

    const fetchAlertasCustom = async (inicio, fin, nuevaPagina = 1) => {
        if (!vehiculoId) return;
        setLoading(true);

        try {
            // 🔥 CORREGIDO: Usando la instancia 'api' con ruta relativa y sin pasar headers manuales
            const res = await api.post(
                "/Alertas/ObtenerAlertaEventos.php",
                {
                    vehiculo_id: vehiculoId,
                    fecha_inicio: formatDate(inicio),
                    fecha_fin: formatDate(fin),
                    limite: LIMITE,
                    offset: (nuevaPagina - 1) * LIMITE
                }
            );

            const data = Array.isArray(res.data.eventos) ? res.data.eventos : [];
            setAlertas(data);
            setPagina(nuevaPagina);
            setTotal(res.data.total || 0);

        } catch (err) {
            console.error("Error cargando alertas:", err.response?.data || err.message);
            setAlertas([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePagina = (num) => {
        fetchAlertasCustom(fechaInicio, fechaFin, num);
    };

    const formatDate = (date) => {
        if (!date) return null;
        return date.toISOString().slice(0, 19).replace('T', ' ');
    };

    const formatFechaPro = (fecha) => {
        const d = new Date(fecha);
        const ahora = new Date();
        const esHoy = d.toDateString() === ahora.toDateString();
        const ayer = new Date(); ayer.setDate(ahora.getDate() - 1);
        const esAyer = d.toDateString() === ayer.toDateString();
        const mismaAnio = d.getFullYear() === ahora.getFullYear();
        const hora = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (esHoy) return `Hoy • ${hora}`;
        if (esAyer) return `Ayer • ${hora}`;
        if (mismaAnio) return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' }) + ` • ${hora}`;
        return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) + ` • ${hora}`;
    };

    const tiempoRelativo = (fecha) => {
        const diff = (new Date() - new Date(fecha)) / 1000;
        if (diff < 60) return "Ahora";
        if (diff < 3600) return `${Math.floor(diff / 60)} min`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
        return null;
    };

    const getColor = (tipo) => {
        switch (tipo) {
            case 'velocidad': return '#FF9800';
            case 'ignicion': return '#4CAF50';
            case 'geocerca':
            case 'geocerca_entrada':
            case 'geocerca_salida': return '#2196F3';
            case 'inactividad': return '#9C27B0';
            default: return '#607D8B';
        }
    };

    const totalPaginas = Math.ceil(total / LIMITE);

    return (
        <>
            <IconButton size="small" onClick={handleOpen}>
                <GoAlertFill color="#2b90fd" />
            </IconButton>

            <Modal open={open} onClose={handleClose}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '95%', maxWidth: 900, bgcolor: '#F9FAFB', borderRadius: 3, boxShadow: 24, display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                    {/* HEADER */}
                    <Box sx={{ p: 3, background: "linear-gradient(135deg, #2b90fd, #1976d2)", color: "#fff" }}>
                        <Typography variant="h6" fontWeight={600}>
                            🚨 Historial de Alertas
                        </Typography>
                        <Typography fontSize={13} sx={{ opacity: 0.8 }}>
                            Eventos generados por el vehículo
                        </Typography>
                    </Box>

                    {/* FILTROS */}
                    <Box sx={{ p: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Stack direction="row" spacing={2}>
                                <DatePicker label="Desde" value={fechaInicio} onChange={setFechaInicio} renderInput={(params) => <TextField {...params} size="small" fullWidth />} />
                                <DatePicker label="Hasta" value={fechaFin} onChange={setFechaFin} renderInput={(params) => <TextField {...params} size="small" fullWidth />} />
                                <IconButton onClick={() => fetchAlertasCustom(fechaInicio, fechaFin, 1)}>🔍</IconButton>
                            </Stack>
                        </LocalizationProvider>
                    </Box>

                    <Divider />

                    {/* LISTA */}
                    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
                        {loading ? (
                            <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                        ) : alertas.length === 0 ? (
                            <Typography align="center" mt={4} color="text.secondary">No hay alertas registradas</Typography>
                        ) : (
                            <Stack spacing={2}>
                                {alertas.map((a) => {
                                    const extra = a.datos_extra || {};
                                    return (
                                        <Paper key={a.id} sx={{ p: 2, borderRadius: 3, borderLeft: `6px solid ${getColor(a.tipo)}`, transition: "0.2s", '&:hover': { transform: "scale(1.01)", boxShadow: 6 } }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography fontWeight={600}>{a.tipo.toUpperCase()}</Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {tiempoRelativo(a.fecha) && <Chip size="small" label={tiempoRelativo(a.fecha)} sx={{ height: 18, fontSize: 10, background: "#E3F2FD", color: "#1976d2" }} />}
                                                    <Chip size="small" label={a.leido ? "Leído" : "Nuevo"} sx={{ background: a.leido ? "#9E9E9E" : "#F44336", color: "#fff" }} />
                                                </Stack>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{formatFechaPro(a.fecha)}</Typography>
                                            </Stack>
                                            <Typography mt={1}> <b>{a.valor}</b></Typography>
                                            {extra && (
                                                <Box mt={1}>
                                                    {extra.geocerca && <Typography variant="body2">📍 {extra.geocerca}</Typography>}
                                                    {extra.limite && <Typography variant="body2">🚧 Límite: {extra.limite}</Typography>}
                                                    {extra.exceso && <Typography variant="body2" color="error">⚠️ Exceso: {extra.exceso}</Typography>}
                                                </Box>
                                            )}
                                        </Paper>
                                    );
                                })}

                                {/* Paginación */}
                                <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
                                    {Array.from({ length: totalPaginas }, (_, i) => (
                                        <Button key={i+1} variant={pagina === i+1 ? "contained" : "outlined"} size="small" onClick={() => handlePagina(i+1)}>
                                            {i+1}
                                        </Button>
                                    ))}
                                </Stack>
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default ModalAlerta;